class VideoGallery extends HTMLElement {
  constructor() {
    super();
    // Don't initialize anything in constructor to avoid DOM errors
    // Instead, wait until connectedCallback when the element is actually in the DOM
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    // Only initialize when the element is connected to the DOM
    this.dialog = this.querySelector(".video-dialog");
    if (!this.dialog) return; // Guard against missing dialog element

    this.slider = this.dialog.querySelector(".video-dialog__slider");
    this.slides = this.dialog.querySelectorAll(".video-dialog__slide");
    this.thumbnails = document.querySelectorAll(".product-videos__video");
    this.closeButton = this.dialog.querySelector(".video-dialog__close");
    this.currentIndex = 0;
    this.isMobile = window.innerWidth <= 768;
    this.videoEndHandlers = []; // Array to store all video ended event handlers

    this.init();
    this.stripThumbnailVideoControls();
    // Run again after a short delay so we catch videos that get src set by the theme's lazy loader
    setTimeout(() => this.stripThumbnailVideoControls(), 400);
    this.initialized = true;
  }

  /**
   * Set video src/poster from data-src/data-poster so the video can play (for lazy-loaded dialog videos).
   */
  activateVideoSources(video) {
    if (!video) return;
    const poster = video.getAttribute("data-poster");
    if (poster) video.poster = poster;
    const sources = video.querySelectorAll("source[data-src]");
    sources.forEach((source) => {
      source.src = source.getAttribute("data-src");
    });
    const videoSrc = video.getAttribute("data-src");
    if (videoSrc) video.src = videoSrc;
  }

  /**
   * Remove native controls from thumbnail strip videos. The HTML5 'controls' attribute
   * is boolean—if present at all (even controls="false"), browsers show the control bar.
   */
  stripThumbnailVideoControls() {
    if (!this.thumbnails) return;
    this.thumbnails.forEach((thumb) => {
      const video = thumb.querySelector("video");
      if (video) {
        video.removeAttribute("controls");
        video.classList.add("no-controls");
      }
    });
  }

  init() {
    // Add click handlers to thumbnails with proper binding and storage
    this.thumbnails.forEach((thumbnail) => {
      const clickHandler = () => {
        this.currentIndex = parseInt(thumbnail.dataset.videoIndex, 10);
        this.openDialog();
      };
      // Store handler reference for cleanup
      thumbnail._clickHandler = clickHandler;
      thumbnail.addEventListener("click", clickHandler);
    });

    // Close dialog handlers
    const closeHandler = () => this.closeDialog();
    this.closeButton.addEventListener("click", closeHandler);
    this.closeButton._clickHandler = closeHandler;

    const dialogClickHandler = (e) => {
      if (e.target === this.dialog) this.closeDialog();
    };
    this.dialog.addEventListener("click", dialogClickHandler);
    this.dialog._clickHandler = dialogClickHandler;

    // Handle dialog open/close events for body scroll
    const openHandler = () => {
      document.body.classList.add("dialog-open");
    };
    this.dialog.addEventListener("open", openHandler);
    this.dialog._openHandler = openHandler;

    const closeEventHandler = () => {
      document.body.classList.remove("dialog-open");
      this.pauseAllVideos(); // Ensure videos in dialog are paused when closed
    };
    this.dialog.addEventListener("close", closeEventHandler);
    this.dialog._closeHandler = closeEventHandler;

    // Setup autoscroll and autoplay/pause
    this.setupDialogVideoAutoplay();
  }

  setupDialogVideoAutoplay() {
    if (!this.slider) return;

    // Bind the scroll handler
    this.boundScrollHandler = this.handleDialogScroll.bind(this);

    // Autoplay/pause videos in dialog on scroll
    this.slider.addEventListener("scroll", this.boundScrollHandler);

    // Set up ended handlers for each video
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector("video");
      if (video) {
        // Create a unique handler for each video
        const endedHandler = () => this._onVideoEnded(index);
        this.videoEndHandlers.push({ video, handler: endedHandler });
        video.addEventListener("ended", endedHandler);
      }
    });
  }

  _onVideoEnded(index) {
    // If it's not the last video, scroll to the next one
    if (index < this.slides.length - 1) {
      this.currentIndex = index + 1;
    } else {
      // If it's the last video, loop back to the first one
      this.currentIndex = 0;
    }
    // In either case, scroll to the selected slide and play it
    this.scrollToSlide(this.currentIndex, true);
  }

  handleDialogScroll() {
    // Only proceed if dialog is open
    if (!this.dialog.open) return;

    const scrollPosition = this.slider.scrollTop;
    const slideHeight = this.slider.clientHeight;
    let newIndex = Math.round(scrollPosition / slideHeight);

    // Ensure newIndex is within bounds
    newIndex = Math.max(0, Math.min(newIndex, this.slides.length - 1));

    if (newIndex !== this.currentIndex) {
      // Pause all videos first to ensure only one plays
      this.pauseAllVideos();
      this.currentIndex = newIndex;
      this.playCurrentVideo(); // Play the new current video
    }
  }

  openDialog() {
    // Check if dialog exists and is in the DOM
    if (!this.dialog || !this.dialog.parentNode) {
      console.error("Dialog element is not in the document");
      return;
    }

    try {
      // Add class to body BEFORE showing modal - but only on mobile
      if (window.innerWidth <= 768) {
        document.body.classList.add("dialog-open");
      }

      this.dialog.showModal();
      this.pauseAllVideos(); // Ensure all videos are paused initially
      this.scrollToSlide(this.currentIndex, true); // Pass true to autoplay the initially opened video
    } catch (error) {
      // If error, remove the class
      document.body.classList.remove("dialog-open");
      console.error("Error opening dialog:", error);
    }
  }

  closeDialog() {
    if (!this.dialog) return;

    this.pauseAllVideos();
    try {
      this.dialog.close();
      // Remove class directly here too, but only needed if on mobile
      if (window.innerWidth <= 768) {
        document.body.classList.remove("dialog-open");
      }
    } catch (error) {
      console.error("Error closing dialog:", error);
    }
  }

  scrollToSlide(index, autoplay = false) {
    if (!this.slider || !this.slides[index]) return;

    const slide = this.slides[index];
    this.slider.scrollTo({
      top: slide.offsetTop,
      behavior: "smooth",
    });

    // Update current index after scrolling starts
    this.currentIndex = index;

    if (autoplay) {
      // Pause all videos first
      this.pauseAllVideos();
      // Delay play to allow scroll to settle and ensure video is in view
      setTimeout(() => this.playCurrentVideo(), 300);
    }
  }

  playCurrentVideo() {
    if (this.currentIndex < 0 || this.currentIndex >= this.slides.length)
      return;

    // Pause all videos first to ensure only one plays at a time
    this.pauseAllVideos();

    const currentVideo = this.slides[this.currentIndex].querySelector("video");
    if (currentVideo) {
      this.activateVideoSources(currentVideo);
      currentVideo.muted = true;
      currentVideo
        .play()
        .catch((e) => console.error("Error attempting to play video:", e));
    }
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector("video");
      if (video && !video.paused) {
        video.pause();
      }
    });
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    // Clean up thumbnail click handlers
    this.thumbnails.forEach((thumbnail) => {
      if (thumbnail._clickHandler) {
        thumbnail.removeEventListener("click", thumbnail._clickHandler);
        delete thumbnail._clickHandler;
      }
    });

    // Clean up dialog event handlers
    if (this.closeButton && this.closeButton._clickHandler) {
      this.closeButton.removeEventListener(
        "click",
        this.closeButton._clickHandler,
      );
    }

    if (this.dialog) {
      if (this.dialog._clickHandler) {
        this.dialog.removeEventListener("click", this.dialog._clickHandler);
      }
      if (this.dialog._openHandler) {
        this.dialog.removeEventListener("open", this.dialog._openHandler);
      }
      if (this.dialog._closeHandler) {
        this.dialog.removeEventListener("close", this.dialog._closeHandler);
      }
    }

    // Clean up scroll handler
    if (this.slider && this.boundScrollHandler) {
      this.slider.removeEventListener("scroll", this.boundScrollHandler);
    }

    // Clean up video ended handlers
    this.videoEndHandlers.forEach(({ video, handler }) => {
      video.removeEventListener("ended", handler);
    });

    this.initialized = false;
  }
}

customElements.define("video-gallery", VideoGallery);
